// src/dev/DevMemberBar.tsx
// Dev-only bar for switching between test members and membership levels
import { useDevMember } from "./DevMemberContext";
import { useOnboardingInspector } from "./OnboardingInspectorContext";
import { useUser } from "@clerk/clerk-react";
import { Bug, User, X, Microscope } from "lucide-react";

type MembershipLevel = "free" | "community" | "customer" | "aaron";

export function DevMemberBar() {
  const {
    isDevMode,
    testMemberName,
    setTestMember,
    testMembers,
    activeTestMember,
    membershipOverride,
    setMembershipOverride,
  } = useDevMember();
  const { isOpen: inspectorOpen, toggleOpen: toggleInspector } = useOnboardingInspector();
  const { user, isSignedIn } = useUser();

  // Only show in dev mode
  if (!isDevMode) return null;

  // Get current membership level - from override if viewing as test member, else from user
  const getCurrentLevel = (): MembershipLevel => {
    if (testMemberName && membershipOverride) {
      // When viewing as test member, use the override
      if (membershipOverride.isAaron) return "aaron";
      if (membershipOverride.isCommunityMember) return "community";
      if (membershipOverride.isCustomer) return "customer";
      return "free";
    }
    // When viewing as myself, show actual user level
    const membership = user?.unsafeMetadata?.membership as any;
    if (membership?.isAaron) return "aaron";
    if (membership?.isCommunityMember) return "community";
    if (membership?.isCustomer) return "customer";
    return "free";
  };

  const handleSetMembershipLevel = async (level: MembershipLevel) => {
    if (testMemberName) {
      // When viewing as test member, use the context override (doesn't touch Clerk)
      setMembershipOverride(level);
    } else if (user) {
      // When viewing as myself, update Clerk directly
      const now = new Date().toISOString();
      let newMembershipData: Record<string, any> | null = null;

      switch (level) {
        case "free":
          newMembershipData = null;
          break;
        case "community":
          newMembershipData = { isCommunityMember: true, memberSince: now, verificationMethod: "dev_toggle" };
          break;
        case "customer":
          newMembershipData = { isCustomer: true, memberSince: now, verificationMethod: "dev_toggle" };
          break;
        case "aaron":
          newMembershipData = { isAaron: true, memberSince: now, verificationMethod: "dev_toggle" };
          break;
      }

      const newUnsafeMetadata = { ...(user.unsafeMetadata || {}) };
      delete newUnsafeMetadata.isCommunityMember;
      delete newUnsafeMetadata.isCustomer;
      delete newUnsafeMetadata.isAaron;
      delete newUnsafeMetadata.planId;
      newUnsafeMetadata.membership = newMembershipData;

      await user.update({ unsafeMetadata: newUnsafeMetadata });
      window.location.reload();
    }
  };

  const currentLevel = getCurrentLevel();
  const isViewingAsTestMember = Boolean(testMemberName);

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-purple-900 text-white px-4 py-2 flex items-center gap-4 text-sm">
      <div className="flex items-center gap-2">
        <Bug className="h-4 w-4" />
        <span className="font-medium">DEV</span>
      </div>

      {/* View As Member Selector - moved first for better UX */}
      <div className="flex items-center gap-2">
        <span className="text-purple-300">View as:</span>
        <select
          value={testMemberName || ""}
          onChange={(e) => setTestMember(e.target.value || null)}
          className="bg-purple-800 border border-purple-600 rounded px-2 py-1 text-white text-sm"
        >
          <option value="">Myself</option>
          {testMembers.map((member) => (
            <option key={member.name} value={member.name}>
              {member.name}
            </option>
          ))}
        </select>
      </div>

      <div className="border-l border-purple-700 h-4 mx-1" />

      {/* Membership Level Selector */}
      {(isSignedIn || isViewingAsTestMember) && (
        <div className="flex items-center gap-2">
          <span className="text-purple-300">Level:</span>
          <div className="flex gap-1">
            {(["free", "customer", "community", "aaron"] as MembershipLevel[]).map((level) => (
              <button
                key={level}
                onClick={() => handleSetMembershipLevel(level)}
                className={`px-2 py-0.5 rounded text-xs capitalize ${
                  currentLevel === level
                    ? "bg-white text-purple-900 font-medium"
                    : "bg-purple-800 hover:bg-purple-700"
                }`}
              >
                {level}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border-l border-purple-700 h-4 mx-1" />

      {/* Inspector Toggle */}
      <button
        onClick={toggleInspector}
        className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
          inspectorOpen
            ? "bg-cyan-500 text-cyan-900 font-medium"
            : "bg-purple-800 hover:bg-purple-700"
        }`}
        title="Toggle Onboarding Inspector"
      >
        <Microscope className="h-3 w-3" />
        Inspector
      </button>

      {activeTestMember && (
        <>
          <div className="flex items-center gap-2 text-purple-300 ml-2">
            <User className="h-4 w-4" />
            <span>
              {activeTestMember.accounts?.length || 0} accounts
            </span>
          </div>

          <button
            onClick={() => setTestMember(null)}
            className="ml-auto flex items-center gap-1 bg-purple-700 hover:bg-purple-600 px-2 py-1 rounded text-xs"
          >
            <X className="h-3 w-3" />
            Exit
          </button>
        </>
      )}
    </div>
  );
}
